import { createSpineInstance } from './spineLoader.js';
import { gameConfig } from '../config/gameConfig.js';

/** Per-symbol-id cap — covers active viewport cells plus scroll headroom without unbounded growth. */
const MAX_POOL_PER_SYMBOL_ID = 16;

/** @type {Map<number, import('@esotericsoftware/spine-pixi-v8').Spine[]>} */
const poolBySymbolId = new Map();

/**
 * @param {import('@esotericsoftware/spine-pixi-v8').Spine} spine
 */
function resetPooledSpine(spine) {
  if (!spine || spine.destroyed) return;
  try {
    spine.state?.clearTracks();
    spine.skeleton?.setToSetupPose();
  } catch {
    /* ignore */
  }
  spine.visible = true;
  spine.alpha = 1;
  if (spine.parent) spine.parent.removeChild(spine);
}

/**
 * @param {number} symbolId
 * @param {Record<number, unknown>} spineAssets
 * @returns {import('@esotericsoftware/spine-pixi-v8').Spine | null}
 */
export function acquireSymbolSpine(symbolId, spineAssets) {
  const list = poolBySymbolId.get(symbolId);
  if (list?.length) {
    const spine = list.pop();
    if (spine && !spine.destroyed) {
      resetPooledSpine(spine);
      return spine;
    }
  }
  return createSpineInstance(symbolId, spineAssets);
}

/**
 * @param {number} symbolId
 * @param {import('@esotericsoftware/spine-pixi-v8').Spine | null} spine
 */
export function releaseSymbolSpine(symbolId, spine) {
  if (!spine || spine.destroyed) return;
  if (!Number.isFinite(symbolId) || symbolId <= 0) {
    spine.destroy();
    return;
  }
  resetPooledSpine(spine);
  const list = poolBySymbolId.get(symbolId) ?? [];
  if (list.length < MAX_POOL_PER_SYMBOL_ID) {
    list.push(spine);
    poolBySymbolId.set(symbolId, list);
    return;
  }
  spine.destroy();
}

/**
 * Create pooled Spines at load so the first turbo spins never hit SkeletonJson on the main thread.
 * @param {Record<number, unknown>} spineAssets
 * @param {{ perId?: number }} [opts]
 */
export function prewarmSymbolSpinePool(spineAssets, opts = {}) {
  if (!spineAssets) return;
  const perId = Math.max(
    1,
    Math.min(MAX_POOL_PER_SYMBOL_ID, Math.floor(Number(opts.perId) || 6)),
  );
  const ids = (gameConfig.symbols ?? [])
    .map((s) => Number(s?.id))
    .filter((id) => Number.isFinite(id) && id > 0);
  for (const symbolId of ids) {
    const list = poolBySymbolId.get(symbolId) ?? [];
    while (list.length < perId) {
      const spine = createSpineInstance(symbolId, spineAssets);
      if (!spine) break;
      resetPooledSpine(spine);
      list.push(spine);
    }
    if (list.length) poolBySymbolId.set(symbolId, list);
  }
}

/** Tear down pooled instances when the game session ends. */
export function drainSymbolSpinePool() {
  for (const list of poolBySymbolId.values()) {
    for (const spine of list) {
      try {
        spine.destroy();
      } catch {
        /* ignore */
      }
    }
  }
  poolBySymbolId.clear();
}
